import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { api, type ItemPage, type SpaceDetail } from '../../lib/api'
import { invaliderFiches } from '../../lib/cache'
import { CategoryMark } from '../spaces/CategoryMark'
import { layout } from '../../design/tokens'

/**
 * Tri par lot des fiches non rangées — planche 04.
 *
 * « Une carte à la fois, trois issues, on enchaîne. » Une grille où l'on classerait dix fiches
 * demanderait de choisir quoi regarder avant de choisir où ranger : deux décisions au lieu
 * d'une. Ici la fiche est imposée, il ne reste qu'à dire où elle va.
 *
 * Aujourd'hui affichait « N idées à classer » sans lien ni écran : un texte qui annonçait
 * quelque chose d'inexistant (correction S57).
 */
export function TriageScreen() {
  const { spaceId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [passees, setPassees] = useState<string[]>([])

  const { data: espace } = useQuery<SpaceDetail>({
    queryKey: ['espace', spaceId],
    queryFn: () => api.space(spaceId),
  })

  const { data, isPending } = useQuery<ItemPage>({
    queryKey: ['fiches', spaceId, 'a-classer'],
    queryFn: () => api.items(spaceId, { status: 'unsorted', limit: 100 }),
  })

  const restantes = (data?.items ?? []).filter((item) => !passees.includes(item.id))
  const courante = restantes[0]

  const rafraichir = async () => {
    await invaliderFiches(queryClient, spaceId)
    await queryClient.invalidateQueries({ queryKey: ['aujourdhui'] })
  }

  const classer = useMutation({
    mutationFn: (categoryId: string) =>
      api.patchItem(spaceId, courante!.id, { categoryId }),
    onSuccess: rafraichir,
  })

  const archiver = useMutation({
    mutationFn: () => api.archiveItem(spaceId, courante!.id),
    onSuccess: rafraichir,
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter, paddingBottom: 32 }}
    >
      <header className="flex items-center gap-3" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="flex shrink-0 items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ←
        </button>
        <h1 className="grow truncate font-serif text-screen">À classer</h1>
        {restantes.length > 0 ? (
          <span className="shrink-0 text-meta text-ink-muted">{restantes.length}</span>
        ) : null}
      </header>

      {isPending ? (
        <p className="text-body text-ink-muted">Chargement…</p>
      ) : courante ? (
        <>
          <article className="rounded-card border border-border bg-card p-4">
            {courante.imageUrl ? (
              <img
                src={courante.imageUrl}
                alt=""
                className="mb-3 w-full rounded-field object-cover"
                style={{ aspectRatio: '4 / 3' }}
              />
            ) : null}
            <h2 className="font-serif text-record font-medium">{courante.title}</h2>
            <p className="mt-2 text-meta text-ink-muted">
              {courante.domain ? `${courante.domain} · ` : ''}
              ajouté par {courante.createdByName}
            </p>
            {courante.description ? (
              <p className="mt-3 text-body" style={{ whiteSpace: 'pre-wrap' }}>
                {courante.description}
              </p>
            ) : null}
          </article>

          <p className="mt-6 text-label font-semibold uppercase text-ink-muted">Ranger dans</p>
          <ul className="mt-3 grid grid-cols-2" style={{ gap: layout.gridGap }}>
            {espace?.categories.map((category) => (
              <li key={category.id}>
                <button
                  type="button"
                  disabled={classer.isPending}
                  onClick={() => classer.mutate(category.id)}
                  className="flex w-full items-center gap-2 rounded-card p-3 text-left disabled:opacity-60"
                  style={{
                    background: `var(--color-cat-${category.hue}-fill)`,
                    minHeight: layout.minimumTouchTarget,
                  }}
                >
                  <CategoryMark initials={category.initials} hue={category.hue} size="small" />
                  <span className="truncate text-meta font-semibold">{category.name}</span>
                </button>
              </li>
            ))}
          </ul>

          {/* Les deux autres issues. « Plus tard » ne touche pas la fiche : elle reste à classer
              et reviendra à la prochaine session, ce qui est le seul comportement honnête pour
              un bouton qui dit « plus tard ». */}
          <button
            type="button"
            onClick={() => archiver.mutate()}
            disabled={archiver.isPending}
            className="mt-6 w-full rounded-button border border-border bg-card text-card-title font-semibold disabled:opacity-60"
            style={{ height: layout.primaryButtonHeight }}
          >
            Archiver
          </button>

          <button
            type="button"
            onClick={() => setPassees((deja) => [...deja, courante.id])}
            className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
            style={{ height: layout.primaryButtonHeight }}
          >
            Plus tard
          </button>
          <p className="mt-2 text-meta text-ink-muted">
            « Plus tard » la laisse à classer : elle reviendra la prochaine fois.
          </p>
        </>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">
            {passees.length > 0 ? 'Plus rien à trancher pour cette fois.' : 'Tout est rangé.'}
          </p>
          <p className="mt-2 text-body text-ink-muted">
            {passees.length > 0
              ? `${passees.length} fiche${passees.length > 1 ? 's' : ''} remise${passees.length > 1 ? 's' : ''} à plus tard.`
              : 'Les nouvelles fiches sans catégorie apparaîtront ici.'}
          </p>
          <button
            type="button"
            onClick={() => void navigate(-1)}
            className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card"
            style={{ height: layout.primaryButtonHeight }}
          >
            Revenir
          </button>
        </div>
      )}
    </main>
  )
}
